// Contrail V3.5.0 — create / edit one alert rule. Used by the per-object Alerts drawer and the Alerts manager (Events tab).
import { FormEvent, useEffect, useState } from "react";
import { supabase } from "../../lib/supabase";
import RecipientPicker from "../../ui/RecipientPicker";
import { ALERT_HELP, ALERT_LABEL, ALERT_ORDER, CHANNEL_LABEL, DAY_SHORT, orgApi, type AlertRule, type AlertType, type Channel, type Zone } from "../../lib/org";
import { Drawer, Field } from "../admin/ui";

export type DevLite = { id: string; name: string; org_id?: string | null };

const MINUTES = [10, 15, 30, 45, 60, 120, 240, 480, 720, 1440];
const minText = (m: number) => (m < 60 ? `${m} minutes` : `${m / 60} hour${m > 60 ? "s" : ""}`);
const REPEATS: [number, string][] = [[0, "Once per time window"], [30, "Every 30 minutes while outside"], [60, "Every hour while outside"], [120, "Every 2 hours while outside"], [240, "Every 4 hours while outside"]];

export default function AlertRuleForm({ org, rule, zones, userEmail, device, devices, onNewZone, onClose, onSaved }: {
  org: string; rule: AlertRule | null; zones: Zone[]; userEmail: string;
  /** fixed object (opened from an object's ⋯ menu) — offers "this object only" vs "every object" */
  device?: DevLite | null;
  /** objects to choose from (opened from the Alerts manager) */
  devices?: DevLite[];
  onNewZone?: () => void; onClose: () => void; onSaved: () => void;
}) {
  const p = rule?.params ?? {};
  const [type, setType] = useState<AlertType>(rule?.type ?? "zone_any");
  const [target, setTarget] = useState<string>(rule ? rule.device_id ?? "" : device?.id ?? "");   // "" = every object
  const [limit, setLimit] = useState<number>(p.limit_kmh ?? 85);
  const [minutes, setMinutes] = useState<number>(p.minutes ?? (rule?.type === "loiter" ? 30 : 60));
  const [zone, setZone] = useState<string>(p.geofence_id ?? "");
  const [where, setWhere] = useState<"any" | "inside" | "outside">(p.where ?? "any");
  const [start, setStart] = useState<string>(p.start ?? "18:00");
  const [end, setEnd] = useState<string>(p.end ?? "06:00");
  const [days, setDays] = useState<number[]>(p.days?.length ? p.days : [0, 1, 2, 3, 4, 5, 6]);
  const [repeat, setRepeat] = useState<number>(p.repeat_min ?? 0);
  const [channels, setChannels] = useState<Channel[]>(rule?.channels ?? (rule ? (rule.recipients.length ? ["email", "inapp"] : ["inapp"]) : ["inapp"]));
  const [to, setTo] = useState<string[]>(rule?.recipients ?? (userEmail ? [userEmail.toLowerCase()] : []));
  const [name, setName] = useState(rule?.name ?? "");
  const [skipDemo, setSkipDemo] = useState<boolean>(rule ? !!p.skip_demo : true);   // V3.9.3: new rules ignore demo devices
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => { // new rule: start from the company's default alert recipients (Company page), if any
    if (rule) return;
    supabase.from("organisations").select("alert_emails").eq("id", org).maybeSingle()
      .then(({ data }) => { if (data?.alert_emails?.length) setTo(data.alert_emails as string[]); });
  }, [org, rule]);

  // zone is optional for "enters or leaves" and speeding, required for the rest
  const zoneMode: "none" | "optional" | "required" =
    type === "zone_enter" || type === "zone_exit" || type === "zone_outside_time" ? "required"
    : type === "zone_any" || type === "overspeed" ? "optional"
    : type === "loiter" && where !== "any" ? "required" : "none";
  useEffect(() => { if (zoneMode === "required" && !zone && zones[0]) setZone(zones[0].id); }, [zoneMode, zone, zones]);

  const email = channels.includes("email");
  const logOnly = channels.includes("log");
  const toggle = (c: Channel) => setChannels((x) => { const y: Channel[] = x.filter((v) => v !== "log"); return y.includes(c) ? y.filter((v) => v !== c) : [...y, c]; });
  const all = email && channels.includes("inapp");

  async function save(e: FormEvent) {
    e.preventDefault();
    if (!channels.length) return setErr("Pick at least one way to be alerted.");
    if (email && !to.length) return setErr("Add at least one email address, or turn Email off.");
    if (type === "zone_outside_time" && !days.length) return setErr("Pick at least one day.");
    setBusy(true); setErr(null);
    const z = zone || undefined;
    const params =
      type === "overspeed" ? { limit_kmh: limit, geofence_id: z ?? null }
      : type === "offline" ? { minutes }
      : type === "zone_enter" || type === "zone_exit" ? { geofence_id: z }
      : type === "zone_any" ? { geofence_id: z ?? null }
      : type === "zone_outside_time" ? { geofence_id: z, start, end, days, repeat_min: repeat }
      : type === "loiter" ? { minutes, where, geofence_id: where === "any" ? null : z }
      : {};
    const withDemo = !target && skipDemo ? { ...params, skip_demo: true } : params;
    const body = { org_id: org, type, device_id: target || null, params: withDemo, name: name.trim() || null, recipients: email ? to : rule?.recipients ?? [], channels };
    try {
      if (rule) await orgApi("PATCH", `alert-rules/${rule.id}`, body); else await orgApi("POST", "alert-rules", body);
      onSaved();
    } catch (x: any) { setErr(x.message); } finally { setBusy(false); }
  }

  const zoneSelect = (allowNone: string | null) => (
    <div className="row-inline">
      <select value={zone} onChange={(e) => setZone(e.target.value)} disabled={!zones.length && !allowNone}>
        {allowNone && <option value="">{allowNone}</option>}
        {zones.map((z) => <option key={z.id} value={z.id}>{z.name}{z.is_global ? " (global)" : ""}</option>)}
      </select>
      {onNewZone && <button type="button" className="btn-ghost small" onClick={onNewZone}>+ New zone</button>}
    </div>
  );

  return (
    <Drawer title={rule ? "Edit alert" : "New alert"} onClose={onClose}
      footer={<><button className="btn-ghost" onClick={onClose}>Back</button>
        <button className="btn-primary" form="rule-form" disabled={busy || (zoneMode === "required" && !zone)}>{busy ? "Saving…" : "Save alert"}</button></>}>
      <form id="rule-form" className="aform" onSubmit={save}>
        {rule?.is_default && <p className="wide sub" style={{ margin: 0 }}>This is one of the company's default alerts. You can change it, switch it off or delete it.</p>}
        <Field label="Alert me when…" wide hint={ALERT_HELP[type]}>
          <select value={type} onChange={(e) => setType(e.target.value as AlertType)} disabled={!!rule}>
            {ALERT_ORDER.map((t) => <option key={t} value={t}>{ALERT_LABEL[t]}</option>)}
          </select>
        </Field>

        {type === "overspeed" && <>
          <Field label="Speed limit (km/h)"><input type="number" min={10} max={250} value={limit} onChange={(e) => setLimit(Number(e.target.value))} /></Field>
          <Field label="Where" wide hint={zones.length ? undefined : "Add a zone to set a zone-specific limit."}>{zoneSelect("Everywhere (global limit)")}</Field>
        </>}
        {type === "zone_any" && <Field label="Zone" wide>{zoneSelect("Any zone (all zones, incl. global)")}</Field>}
        {(type === "zone_enter" || type === "zone_exit") && <Field label="Zone" wide hint={zones.length ? undefined : "Create a zone first."}>{zoneSelect(null)}</Field>}
        {type === "zone_outside_time" && <>
          <Field label="Should be inside" wide hint={zones.length ? undefined : "Create a zone first."}>{zoneSelect(null)}</Field>
          <Field label="From (Fiji time)"><input type="time" value={start} onChange={(e) => setStart(e.target.value)} required /></Field>
          <Field label="To"><input type="time" value={end} onChange={(e) => setEnd(e.target.value)} required /></Field>
          <Field label="Days" wide>
            <div className="chips">
              {[1, 2, 3, 4, 5, 6, 0].map((d) => <button type="button" key={d} className={days.includes(d) ? "on" : ""} aria-pressed={days.includes(d)}
                onClick={() => setDays((x) => (x.includes(d) ? x.filter((y) => y !== d) : [...x, d]))}>{DAY_SHORT[d]}</button>)}
              <button type="button" className="linklike" onClick={() => setDays([1, 2, 3, 4, 5])}>Weekdays</button>
              <button type="button" className="linklike" onClick={() => setDays([0, 1, 2, 3, 4, 5, 6])}>Every day</button>
            </div>
          </Field>
          <Field label="Alert" wide>
            <select value={repeat} onChange={(e) => setRepeat(Number(e.target.value))}>
              {REPEATS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </Field>
        </>}
        {type === "loiter" && <>
          <Field label="Not moving for">
            <select value={minutes} onChange={(e) => setMinutes(Number(e.target.value))}>
              {MINUTES.map((m) => <option key={m} value={m}>{minText(m)}</option>)}
            </select>
          </Field>
          <Field label="Where">
            <select value={where} onChange={(e) => setWhere(e.target.value as typeof where)}>
              <option value="any">Anywhere</option><option value="inside">Inside a zone</option><option value="outside">Outside a zone</option>
            </select>
          </Field>
          {where !== "any" && <Field label="Zone" wide hint={zones.length ? undefined : "Create a zone first."}>{zoneSelect(null)}</Field>}
        </>}
        {type === "offline" && <Field label="No report for">
          <select value={minutes} onChange={(e) => setMinutes(Number(e.target.value))}>
            {[15, 30, 60, 120, 240, 720, 1440].map((m) => <option key={m} value={m}>{minText(m)}</option>)}
          </select></Field>}

        {device ? (
          <fieldset className="wide aradio">
            <legend>Applies to</legend>
            <label><input type="radio" checked={target === device.id} onChange={() => setTarget(device.id)} /><span><b>{device.name}</b> only</span></label>
            <label><input type="radio" checked={target === ""} onChange={() => setTarget("")} /><span>Every object in the company (including future ones)</span></label>
          </fieldset>
        ) : (
          <Field label="Applies to" wide>
            <select value={target} onChange={(e) => setTarget(e.target.value)}>
              <option value="">Every object in the company (including future ones)</option>
              {(devices ?? []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </Field>
        )}

        {!target && <label className="wide check demo-skip">
          <input type="checkbox" checked={skipDemo} onChange={(e) => setSkipDemo(e.target.checked)} />
          <span>Ignore demo devices <span className="sub">— simulated objects won't trigger this alert</span></span>
        </label>}

        <Field label="Alert by" wide hint={logOnly ? "Log only: recorded in the Events tab (already marked seen) — no badge, no email." : channels.includes("inapp") ? "In-app alerts show in the Events tab with a badge." : "Email only — these won't show in the Events tab."}>
          <div className="chips ch-pick">
            <button type="button" className={all ? "on" : ""} aria-pressed={all} onClick={() => setChannels(all ? ["inapp"] : ["email", "inapp"])}>All</button>
            {(["email", "inapp"] as Channel[]).map((c) => <button type="button" key={c} className={channels.includes(c) ? "on" : ""} aria-pressed={channels.includes(c)} onClick={() => toggle(c)}>{CHANNEL_LABEL[c]}</button>)}
            <button type="button" disabled title="Phone app notifications are coming with the Contrail mobile app">{CHANNEL_LABEL.push} · soon</button>
            <button type="button" className={logOnly ? "on" : ""} aria-pressed={logOnly} onClick={() => setChannels(logOnly ? ["inapp"] : ["log"])}
              title="Keep a record of these events without notifying anyone">{CHANNEL_LABEL.log}</button>
          </div>
        </Field>
        {email && <Field label="Email to" wide hint="Pick company users or contacts, or type an address.">
          <RecipientPicker org={org} value={to} onChange={setTo} />
        </Field>}
        <Field label="Name (optional)" wide><input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} placeholder="e.g. Depot after hours" /></Field>
        {err && <div className="error wide">{err}</div>}
      </form>
    </Drawer>
  );
}
